import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  activatePhases,
  capabilities,
  deliverableGroups,
  faqs,
  guideNavigation,
  howItWorks,
  quickstarts,
  valuePillars,
} from '../data/selectivateGuideContent';

const sectionIconMap = {
  overview: <MenuBookIcon />,
  'sap-activate': <TimelineIcon />,
  deliverables: <DescriptionIcon />,
  'how-it-works': <AssignmentIcon />,
  quickstarts: <RocketLaunchIcon />,
  faq: <HelpOutlineIcon />,
};

const phaseIconMap = {
  Prepare: <RocketLaunchIcon />,
  Explore: <FindInPageIcon />,
  Realize: <BuildIcon />,
  Deploy: <AssignmentIcon />,
  Run: <CheckCircleIcon />,
};

const scrollToSection = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const SectionHeader = ({ id, eyebrow, title, description }) => (
  <Box id={id} sx={{ scrollMarginTop: 96, mb: 3 }}>
    <Typography
      variant="overline"
      sx={{
        color: 'primary.main',
        fontWeight: 800,
        letterSpacing: 0,
        display: 'block',
        mb: 0.75,
        fontSize: '0.84rem',
        lineHeight: 1.2,
      }}
    >
      {eyebrow}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
      {title}
    </Typography>
    {description && (
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 860, lineHeight: 1.7 }}>
        {description}
      </Typography>
    )}
  </Box>
);

const DocumentationHomePage = () => {
  const theme = useTheme();
  const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'));
  const [query, setQuery] = useState('');

  const searchableItems = useMemo(() => {
    const items = [
      ...guideNavigation.map((item) => ({ type: 'Section', title: item.label, target: item.id })),
      ...capabilities.map((item) => ({ type: 'Capability', title: item.title, body: item.description, target: 'overview' })),
      ...deliverableGroups.flatMap((group) =>
        group.items.map((item) => ({ type: 'Deliverable', title: item, body: group.phase, target: 'deliverables' }))
      ),
      ...quickstarts.map((item) => ({ type: 'Quickstart', title: item.title, body: item.steps.join(' '), target: 'quickstarts' })),
      ...faqs.map((item) => ({ type: 'FAQ', title: item.question, body: item.answer, target: 'faq' })),
    ];

    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return [];

    return items
      .filter((item) => `${item.title} ${item.body || ''}`.toLowerCase().includes(normalizedQuery))
      .slice(0, 6);
  }, [query]);

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        bgcolor: 'background.default',
        backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 260px)`,
        py: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={3} alignItems="stretch">
          {!isBelowLg && (
            <Grid item lg={2.15} xl={2} sx={{ display: 'flex' }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 96,
                  alignSelf: 'flex-start',
                  maxHeight: 'calc(100vh - 120px)',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    p: 0,
                    pr: 2,
                    border: 0,
                    borderRight: `1px solid ${alpha(theme.palette.common.white, 0.035)}`,
                    borderRadius: 0,
                    bgcolor: 'transparent',
                    overflowY: 'auto',
                    maxHeight: 'inherit',
                    boxShadow: 'none',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      px: 0.75,
                      py: 0.5,
                      mb: 0.5,
                      fontWeight: 750,
                      fontSize: '0.9rem',
                      lineHeight: 1.3,
                    }}
                  >
                    Selectivate Guide
                  </Typography>
                  <List dense disablePadding>
                    {guideNavigation.map((item) => (
                      <ListItemButton
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        sx={{
                          borderRadius: 0.75,
                          minHeight: 38,
                          px: 0.75,
                          py: 0.55,
                          mb: 0.15,
                          color: 'text.secondary',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.07),
                            color: 'text.primary',
                          },
                        }}
                      >
                        <Box sx={{ color: 'primary.main', display: 'flex', mr: 1.1, minWidth: 18 }}>
                          {React.cloneElement(sectionIconMap[item.id], { sx: { fontSize: 17 } })}
                        </Box>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: {
                              fontSize: '0.9rem',
                              fontWeight: 650,
                              lineHeight: 1.25,
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              </Box>
            </Grid>
          )}

          <Grid item xs={12} lg={9.85} xl={10}>
            <Box sx={{ maxWidth: 1120, mx: { xs: 0, xl: 'auto' } }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Docs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  /
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Selectivate Guide
                </Typography>
              </Stack>

              <Box sx={{ mb: 5 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, mb: 1.5, fontSize: { xs: '2rem', md: '2.7rem' } }}>
                  <Box component="span" sx={{ color: 'primary.main' }}>S</Box>electivate Guide
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.75, maxWidth: 920 }}>
                  Learn how Selectivate helps SAP project teams turn project inputs into structured SAP Activate deliverables.
                </Typography>
              </Box>

              <TextField
                fullWidth
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search guide topics, deliverables, quickstarts, or FAQs"
                size="small"
                sx={{ mb: 5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              {searchableItems.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: -4,
                    mb: 5,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                    overflow: 'hidden',
                  }}
                >
                  {searchableItems.map((item) => (
                    <ListItemButton key={`${item.type}-${item.title}`} onClick={() => scrollToSection(item.target)}>
                      <Chip label={item.type} size="small" sx={{ mr: 1.5, minWidth: 88 }} />
                      <ListItemText primary={item.title} secondary={item.body} />
                    </ListItemButton>
                  ))}
                </Paper>
              )}

              <SectionHeader
                id="overview"
                eyebrow="Overview"
                title="What Selectivate does"
                description="Selectivate brings project documents, templates, requirements, and SAP delivery workflows into one workspace. It helps teams create consistent, review-ready outputs while keeping consultants in control of final validation."
              />
              <Grid container spacing={2} sx={{ mb: 5 }}>
                {valuePillars.map((pillar) => (
                  <Grid item xs={12} md={4} key={pillar.title}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                        bgcolor: alpha(theme.palette.common.white, 0.03),
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                          {pillar.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          {pillar.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2} sx={{ mb: 5 }}>
                {capabilities.map((capability) => (
                  <Grid item xs={12} md={6} key={capability.title}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                        bgcolor: alpha(theme.palette.common.white, 0.03),
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                          {capability.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          {capability.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <SectionHeader
                id="sap-activate"
                eyebrow="SAP Activate"
                title="How Selectivate fits the delivery lifecycle"
                description="Selectivate organizes work around the phases clients and consultants already recognize."
              />
              <Stack spacing={2} sx={{ mb: 5 }}>
                {activatePhases.map((phase) => (
                  <Paper
                    key={phase.name}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderLeft: 5,
                      borderColor: phase.color,
                      bgcolor: alpha(phase.color, 0.08),
                      borderTop: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
                      borderRight: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
                      borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                      <Box sx={{ color: phase.color, display: 'flex' }}>
                        {React.cloneElement(phaseIconMap[phase.name], { fontSize: 'large' })}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          {phase.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          {phase.summary}
                        </Typography>
                      </Box>
                      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ maxWidth: { md: 420 } }}>
                        {phase.outputs.map((output) => (
                          <Chip key={output} label={output} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>

              <SectionHeader
                id="deliverables"
                eyebrow="Deliverables"
                title="What Selectivate can help produce"
                description="A quick view of the outputs clients and project teams can recognize immediately."
              />
              <Grid container spacing={2} sx={{ mb: 5 }}>
                {deliverableGroups.map((group) => (
                  <Grid item xs={12} md={6} key={group.phase}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        borderLeft: 5,
                        borderColor: group.color,
                        bgcolor: alpha(group.color, 0.08),
                        borderTop: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
                        borderRight: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
                        borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
                        {group.phase}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {group.items.map((item) => (
                          <Chip key={item} label={item} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <SectionHeader
                id="how-it-works"
                eyebrow="How It Works"
                title="From project input to usable output"
                description="A simple flow for explaining Selectivate in demos and onboarding."
              />
              <Grid container spacing={2} sx={{ mb: 5 }}>
                {howItWorks.map((step, index) => (
                  <Grid item xs={12} sm={6} md={index === howItWorks.length - 1 ? 12 : 6} key={step}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        gap: 2,
                        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                        bgcolor: alpha(theme.palette.common.white, 0.03),
                      }}
                    >
                      <Chip label={index + 1} color="primary" sx={{ fontWeight: 800 }} />
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        {step}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <SectionHeader
                id="quickstarts"
                eyebrow="Quickstarts"
                title="Start with a common task"
                description="Action-oriented entry points for the most common Selectivate workflows."
              />
              <Grid container spacing={2} sx={{ mb: 5 }}>
                {quickstarts.map((quickstart) => (
                  <Grid item xs={12} md={6} key={quickstart.title}>
                    <Accordion
                      disableGutters
                      sx={{
                        bgcolor: alpha(theme.palette.common.white, 0.03),
                        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                        '&::before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 800 }}>{quickstart.title}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={1}>
                          {quickstart.steps.map((step, index) => (
                            <Stack key={step} direction="row" spacing={1.5} alignItems="flex-start">
                              <Chip label={index + 1} size="small" />
                              <Typography variant="body2" color="text.secondary">
                                {step}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                ))}
              </Grid>

              <SectionHeader
                id="faq"
                eyebrow="FAQ"
                title="Common client questions"
                description="Short answers for expectations around AI assistance, inputs, review, and phase coverage."
              />
              <Stack spacing={1.5}>
                {faqs.map((faq) => (
                  <Accordion
                    key={faq.question}
                    disableGutters
                    sx={{
                      bgcolor: alpha(theme.palette.common.white, 0.03),
                      border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                      '&::before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 800 }}>{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default DocumentationHomePage;
